import {Injectable} from '@angular/core';
import {Observable, of,} from 'rxjs';
import { HttpClient } from '@angular/common/http';
import {ApiLocationService} from '@services/api-location/api-location.service';
import {HTTPS_REQUEST_OPTIONS} from '@constants/https-request-options/https-request-options';
import {FilterEvent} from '@models/pages/status-event-group-page/header-filter-event';
import {
  StatusEventGroupPageDisplayDetails, StatusEventPageData
} from '@models/pages/status-event-group-page/status-event-group-page-display-details';
import {
  StatusEventPageEventDisplayDetails
} from '@models/pages/status-event-page/status-event-page-event-display-details';
import {shareReplay} from 'rxjs/operators';
import {StatusGroupPageData} from '@models/dashboard/dashboard-group';

@Injectable({
  providedIn: 'root'
})
export class StatusEventGroupPageDataService {

  private readonly BASE_URL: string;

  private GET_STATUS_EVENT_PAGE_URL_SUFFIX = '/api/web/base/:machineGroupId/machineGroupStatusEventPage/:pageId/data/:timestamp/:sampling';
  private MINIMAL_DATA_URL_SUFFIX: string = '/api/web/tenant/0/machine/minimaldata';

  private statusEventGroupPageInfoResponseCache = new Map();

  constructor(
    private http: HttpClient,
    private apiLocation: ApiLocationService,
  ) {
    this.BASE_URL = this.apiLocation.getLocation();
  }

  public getStatusEventGroupPageData(groupId: number, pageId: number, timestamp: number = 0, sampling: number = 1): Observable<any> {
    let url: string = this.BASE_URL + this.GET_STATUS_EVENT_PAGE_URL_SUFFIX;
    url = url.replace(':machineGroupId', String(groupId))
      .replace(':pageId', String(pageId))
      .replace(':timestamp', String(timestamp))
      .replace(':sampling', String(sampling));
    return this.http.get(url, HTTPS_REQUEST_OPTIONS);
  }

  public getMinimalStatusEventGroupPageInfoByBulk(machineryGroupsIds: number[], isFirstBatch: boolean): Observable<any> {
    const url: string = this.BASE_URL + this.MINIMAL_DATA_URL_SUFFIX;
    const statusEventGroupPageInfoResponseCache = this.statusEventGroupPageInfoResponseCache.get(isFirstBatch ? url + '/1' : url + '/2');
    if (statusEventGroupPageInfoResponseCache) {
      return of(statusEventGroupPageInfoResponseCache);
    }
    const response = this.http.post<{[key: string]: StatusGroupPageData}>(url, machineryGroupsIds, HTTPS_REQUEST_OPTIONS).pipe(shareReplay({
      bufferSize: 2,
      refCount: true
    }));
    response.subscribe((result) => {
      this.statusEventGroupPageInfoResponseCache.set(isFirstBatch ? url + '/1' : url + '/2', result);
    });
    return response;
  }

  public updateEventsPagination(allEvents: StatusEventGroupPageDisplayDetails[], pageNumber: number, itemPerPage: number, filters: FilterEvent): StatusEventGroupPageDisplayDetails[] {
    const arr: any[] = [];
    const filteredEvents = this.filterEvents(allEvents, filters);
    const start = pageNumber * itemPerPage;
    const end = ((pageNumber + 1) * itemPerPage) - 1;
    for (let i = start; i <= end; i++) {
      if (!filteredEvents[i]) {
        break;
      }
      arr.push(filteredEvents[i]);
    }

    return arr;
  }

  public parseStatusEventResponseToDisplayDetails(response: StatusEventPageData[]): StatusEventPageEventDisplayDetails[] {
    const result: StatusEventPageEventDisplayDetails[] = [];
    response.forEach(({signalName, events}) => {
      events.forEach(({ts, values}) => {
        values.forEach(({code, type, description, metadata, localizations}) => {
          const item: StatusEventPageEventDisplayDetails = new StatusEventPageEventDisplayDetails(
            signalName, signalName, ts, code, type, description, metadata, localizations
          );
          result.push(item);
        });
      });
    });

    return result.sort((pre, next) => next.timestamp - pre.timestamp);
  }

  filterEvents(allEvents: StatusEventGroupPageDisplayDetails[], $event: FilterEvent): StatusEventGroupPageDisplayDetails[] {
    const filterKeys = Object.keys($event).filter(item => $event[item]);
    const arr: StatusEventGroupPageDisplayDetails[] = [];
    allEvents.forEach(event => {
      const {
        machineId,
        machineLocation,
        machineName,
        statusEventPageData
      }: StatusEventGroupPageDisplayDetails = JSON.parse(JSON.stringify(event));
      statusEventPageData.forEach((signalData) => {
        signalData.events = signalData.events.filter(({values}) => filterKeys.includes(values[0].type));
      });

      arr.push(new StatusEventGroupPageDisplayDetails(
        machineName,
        machineId,
        machineLocation,
        statusEventPageData
      ));
    });

    return arr;
  }
}
